import { useEffect, useRef, useState } from 'react';

import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { Skeleton } from '../../../../../Skeleton';
import { useImagePreload } from '../../../../../hooks';
import { useHeight } from '../hooks';
import { MediaEmpty } from '../../../../../MediaEmpty';
import { Icon } from '../../../../../Icon';

interface ImagesGallerySliderProps {
  images: {
    src: string;
    name: string;
    id: string;
  }[];
  hasError: boolean;
  editable: boolean;
}

export const ImagesGallerySlider = ({
  images,
  editable,
  hasError = false,
}: ImagesGallerySliderProps) => {
  const { editorHeight, maxHeight } = useHeight();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<
    Record<string, { loading: boolean; fulfilled: boolean; rejected: boolean }>
  >({});
  const preloadCache = useRef<Record<string, boolean>>({});

  const staticHeight = editable ? editorHeight : maxHeight;

  const currentSrc = images[currentIndex]?.src;
  const imageStatus = useImagePreload(currentSrc, { cache: preloadCache });

  // Предзагрузка изображений
  useEffect(() => {
    if (!currentSrc || imageStatus === 'idle') return;

    preloadCache.current[currentSrc] = true;

    setLoadedImages((prev) => ({
      ...prev,
      [currentSrc]: {
        loading: imageStatus === 'loading',
        fulfilled: imageStatus === 'complete',
        rejected: imageStatus === 'failed',
      },
    }));
  }, [currentSrc, imageStatus]);

  const goToNext = (index: number) => {
    if (images[index]?.src) {
      setCurrentIndex(index);
    }
  };

  const getCurrentImage = (status?: {
    loading: boolean;
    fulfilled: boolean;
    rejected: boolean;
  }) => {
    if (status?.rejected) {
      return <MediaEmpty height={staticHeight} radius="m" />;
    }

    if (status?.fulfilled) {
      return (
        <img
          src={images[currentIndex].src}
          alt={`Slide ${currentIndex}`}
          style={{
            maxWidth: '100%',
            height: staticHeight,
            objectFit: 'contain',
          }}
          loading="lazy"
        />
      );
    }

    return <Skeleton height={staticHeight} width="100%" variant="rect" />;
  };

  if (!Object.keys(loadedImages).length) {
    return <Skeleton height={staticHeight} width="100%" variant="rect" />;
  }

  if (hasError) {
    return <MediaEmpty height={staticHeight} variant="multiple" radius="m" />;
  }

  return (
    <Box
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor={
        loadedImages[images[currentIndex].src]
          ? 'bg.dark.primary'
          : 'bg.light.primary'
      }
      radius="m"
      overflow="hidden"
    >
      {getCurrentImage(loadedImages[images[currentIndex].src])}

      <Box
        position="absolute"
        top="calc(50% - 16px)"
        left={20}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={32}
        height={32}
        radius="round"
        bgcolor="bg.dark.secondary"
        color="text.dark.primary"
        cursor={currentIndex === 0 ? 'default' : 'pointer'}
        onClick={() => goToNext(currentIndex - 1)}
      >
        <Icon
          name="chevron_left_small_outline"
          size="24"
          color="text.dark.primary"
        />
      </Box>
      <Box
        position="absolute"
        top="calc(50% - 16px)"
        right={20}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={32}
        height={32}
        radius="round"
        bgcolor="bg.dark.secondary"
        color="text.dark.primary"
        cursor={currentIndex === images.length - 1 ? 'default' : 'pointer'}
        onClick={() => goToNext(currentIndex + 1)}
      >
        <Icon
          name="chevron_right_small_outline"
          size="24"
          color="text.dark.primary"
        />
      </Box>

      <Box
        position="absolute"
        bottom={16}
        left="50%"
        display="flex"
        alignItems="center"
        gap="2"
        px="8"
        py="2"
        transform="translateX(-50%)"
        radius="l"
        bgcolor="bg.dark.secondary"
      >
        <Typography variant="overline" color="text.dark.primary">
          {currentIndex + 1}
        </Typography>
        <Typography variant="overline" color="text.dark.primary">
          /
        </Typography>
        <Typography variant="overline" color="text.dark.primary">
          {images.length}
        </Typography>
      </Box>
    </Box>
  );
};
